import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { getTimer, startTimerStore, subscribeTimer } from "@/lib/timer";
import { saveTimerEntry } from "@/components/TimerWidget";
import { fmtDecimalHours, fmtElapsed, fmtMoney } from "@/lib/time";
import { formatDate } from "@/lib/dates";
import { TimeEntryDialog, ConfirmDelete } from "@/components/TimeExpenseSections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Plus, Square, Timer as TimerIcon, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";

export default function Timer() {
  const { t } = useT();
  const [matters, setMatters] = useState([]);
  const [matterId, setMatterId] = useState("");
  const [description, setDescription] = useState("");
  const [timer, setTimer] = useState(getTimer());
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [recent, setRecent] = useState([]);

  const loadRecent = () => {
    api.get("/time-entries").then((r) => setRecent(r.data)).catch(() => {});
  };
  useEffect(loadRecent, []);

  useEffect(() => {
    api.get("/matters").then((r) => setMatters(r.data.filter((m) => m.status !== "Closed"))).catch(() => {});
  }, []);
  useEffect(() => subscribeTimer(() => setTimer(getTimer())), []);
  useEffect(() => {
    if (!timer) return;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [timer]);

  const start = () => {
    if (!matterId) {
      toast.error(t("timer.select_first"));
      return;
    }
    if (timer) {
      toast.error(t("timer.already_running"));
      return;
    }
    const matter = matters.find((m) => m.id === matterId);
    startTimerStore({
      matter_id: matterId,
      matter_name: matter?.name || "",
      description: description.trim(),
      started_at: Date.now(),
    });
    toast.success(t("timer.started"));
  };

  const stop = async () => {
    setBusy(true);
    try {
      const entry = await saveTimerEntry(timer);
      toast.success(
        t("timer.saved", { h: (entry.billable_minutes / 60).toFixed(1), amount: entry.amount.toFixed(2) })
      );
      loadRecent();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="timer-page" className="max-w-xl space-y-6">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-slate-900">{t("timer.title")}</h1>
        <p className="text-sm text-slate-500 mt-1">
          {t("timer.subtitle")}
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5 shadow-sm">
        {timer ? (
          <div className="text-center space-y-4" data-testid="timer-running-panel">
            <div className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
              </span>
              {t("timer.recording")}
            </div>
            <div className="font-mono text-5xl text-slate-900" data-testid="timer-elapsed">
              {fmtElapsed(now - timer.started_at)}
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-800">{timer.matter_name}</div>
              {timer.description && <div className="text-xs text-slate-500 mt-0.5">{timer.description}</div>}
            </div>
            <Button
              data-testid="timer-stop-button"
              onClick={stop}
              disabled={busy}
              className="bg-red-700 hover:bg-red-600"
            >
              <Square className="h-4 w-4 mr-1.5" /> {busy ? t("timer.saving") : t("timer.stop")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="timer-start-panel">
            <div>
              <Label>{t("timer.matter")} <span className="text-red-600">*</span></Label>
              <Select value={matterId} onValueChange={setMatterId}>
                <SelectTrigger data-testid="timer-matter-select" className="mt-1.5">
                  <SelectValue placeholder={t("timer.select_matter")} />
                </SelectTrigger>
                <SelectContent>
                  {matters.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`timer-matter-option-${m.internal_ref || m.id}`}>
                      {m.name}
                      {m.internal_ref ? ` (${m.internal_ref})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="timer-desc">{t("timer.short_description")}</Label>
              <Input
                id="timer-desc"
                data-testid="timer-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("timer.description_ph")}
                className="mt-1.5"
              />
            </div>
            <Button
              data-testid="timer-start-button"
              onClick={start}
              className="bg-slate-900 hover:bg-slate-800 w-full"
            >
              <Play className="h-4 w-4 mr-1.5" /> {t("timer.start")}
            </Button>
            <p className="text-xs text-slate-400 flex items-center gap-1.5">
              <TimerIcon className="h-3.5 w-3.5" /> {t("timer.keep_running")}
            </p>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="manual-time-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h2 className="font-serif text-lg text-slate-800">{t("timer.manual_title")}</h2>
          <Button
            data-testid="add-time-button"
            onClick={() => setManualOpen(true)}
            className="bg-slate-900 hover:bg-slate-800"
          >
            <Plus className="h-4 w-4 mr-1.5" /> {t("time.add_time")}
          </Button>
        </div>
        {recent.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="no-recent-time">{t("time.none")}</p>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="recent-time-entries">
            {recent.map((e2) => (
              <div key={e2.id} className="py-2.5 flex items-center justify-between gap-3"
                data-testid={`recent-time-row-${e2.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{e2.description}</div>
                  <div className="text-xs text-slate-500">{formatDate(e2.date)} · {e2.matter_name}</div>
                  {e2.billed_invoice_id ? (
                    <Link to={`/invoices/${e2.billed_invoice_id}`} data-testid={`recent-time-invoice-${e2.id}`}
                      className="inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200 hover:underline">
                      {t("entry.in_invoice", { number: e2.billed_invoice_number })}
                    </Link>
                  ) : e2.allocated_invoice_id ? (
                    <div className="mt-1 text-[11px] text-amber-700" data-testid={`recent-time-draft-${e2.id}`}>
                      {t("entry.in_draft", { number: e2.allocated_invoice_number })}
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="text-xs text-slate-600 text-right">
                    <div className="font-semibold" data-testid={`recent-time-type-${e2.id}`}>
                      {(e2.billing_type || "Hourly") !== "Hourly" ? t(`val.${e2.billing_type}`) : fmtDecimalHours(e2.billable_minutes)}
                    </div>
                    <div>{fmtMoney(e2.amount)}</div>
                  </div>
                  <button data-testid={`recent-time-edit-${e2.id}`} disabled={Boolean(e2.billed_invoice_id)}
                    title={e2.billed_invoice_id ? t("entry.billed_locked") : t("common.edit")}
                    onClick={() => setEditEntry(e2)}
                    className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:text-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button data-testid={`recent-time-delete-${e2.id}`} disabled={Boolean(e2.billed_invoice_id)}
                    title={e2.billed_invoice_id ? t("entry.billed_locked") : t("common.delete")}
                    onClick={() => setDeleteEntry(e2)}
                    className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:text-red-700 hover:border-red-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <TimeEntryDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        matter={null}
        matters={matters}
        initial={null}
        onSaved={() => {
          setManualOpen(false);
          loadRecent();
          toast.success(t("time.added"));
        }}
      />
      <TimeEntryDialog
        open={Boolean(editEntry)}
        onOpenChange={(o) => !o && setEditEntry(null)}
        matter={null}
        matters={matters}
        initial={editEntry}
        onSaved={() => {
          setEditEntry(null);
          loadRecent();
          toast.success(t("time.updated"));
        }}
      />
      <ConfirmDelete
        open={Boolean(deleteEntry)}
        onOpenChange={(o) => !o && setDeleteEntry(null)}
        testid="recent-time-delete-confirm"
        body={deleteEntry
          ? t("time.delete_confirm") +
            (deleteEntry.allocated_invoice_number
              ? t("entry.draft_usage_delete", { number: deleteEntry.allocated_invoice_number })
              : "")
          : ""}
        onConfirm={async () => {
          const entry = deleteEntry;
          setDeleteEntry(null);
          try {
            await api.delete(`/time-entries/${entry.id}`);
            loadRecent();
            toast.success(t("time.deleted"));
          } catch (err) {
            toast.error(formatApiError(err));
          }
        }}
      />
    </div>
  );
}
